import React, { useEffect } from 'react';
import { graphql } from 'gatsby';
import SEO from '@components/Common/SEO/index';
import PPCNavbar from '@components/PPCNavbar';
import FooterV2 from '@components/FooterV2/index';
import BottomStrip from '@components/PPCNavbar/BottomStrip';
import VideoTestimonialSection from '@components/v5/VideoTestimonialSection/index';
import MentorTestimonialsSection from '@components/MentorSection';
import FormSubmitButton from '@components/LeadGenerationForm/FormComponents/FormSubmitButton';
import useSale from '@src/hooks/useSale';
import { GTM } from '@src/analytics/gtm';
import { gtmEvents } from '@src/analytics/gtmEventsConstant';

import * as styles from '@components/v5/AiEngineerFellowship/aiEngineerFellowship.module.scss';
import Hero from '@components/v5/AiEngineerFellowship/Hero';
import Curriculum from '@components/v5/AiEngineerFellowship/Curriculum';
import DeployedShowcase from '@components/v5/AiEngineerFellowship/DeployedShowcase';

const PROGRAM_ID = 'AI_ENGINEER_FELLOWSHIP';
const SLUG = 'ai-engineering';

// Slim landing page: hero + form, community/video testimonials and mentors
// (same components as /provisional-enrollment/software-development-fellowship-program/),
// then the curriculum and production-projects sections from /ai-engineering/.
// PPCNavbar auto-detects any "/provisional-enrollment/..." path and renders
// just a centered Crio.Do logo. Content comes from the same YAML node as the
// main page.
export default function AiEngineerFellowshipProvisionalEnrollment({ data }) {
  const { sale } = useSale();
  const content = data.aiEngineeringPagesYaml;

  useEffect(() => {
    GTM.track(gtmEvents.PAGE_VIEW_FSD, {
      type: PROGRAM_ID,
      slug: SLUG,
    });
    GTM.track(gtmEvents.PAGE_VIEW, {
      type: PROGRAM_ID,
      slug: SLUG,
    });
  }, []);

  useEffect(() => {
    const previous = document.documentElement.style.scrollBehavior;
    document.documentElement.style.scrollBehavior = 'smooth';
    return () => {
      document.documentElement.style.scrollBehavior = previous;
    };
  }, []);

  return (
    <div id="top">
      <SEO
        title={content.SEOTitle}
        description={content.SEODescription}
        canonicalUrl={content.canonicalUrl}
      />

      <PPCNavbar />

      <Hero sale={sale} content={content.hero} />

      <div className="bg-white">
        <VideoTestimonialSection
          quote={
            <>
              Get more insights on how Crio can help you{' '}
              <br className="hidden sm:block" />
              crack an AI Engineer role in top tech companies.
            </>
          }
          postContent={
            <div className="flex items-center justify-center">
              <a href="#top">
                <FormSubmitButton text="Confirm Your Seat" className="" />
              </a>
            </div>
          }
        />
      </div>

      <div className={styles.page}>
        <Curriculum content={content.curriculum} compact />
      </div>

      <div className="bg-white">
        <div id="mentor-section" className="section-target"></div>
        <MentorTestimonialsSection />
      </div>

      <div className={styles.page}>
        <DeployedShowcase apps={content.deployedShowcase.apps} />
      </div>

      <FooterV2 />
      <BottomStrip />
    </div>
  );
}

export const query = graphql`
  query {
    aiEngineeringPagesYaml {
      SEOTitle
      SEODescription
      canonicalUrl
      hero {
        titleHtml
        subtitle
        descriptionHtml
        tags {
          icon
          label
        }
      }
      curriculum {
        sprints {
          id
          title
          wk
          lead
          pts
          ship
          chips
          highlight
        }
        dsaModules {
          name
          dur
        }
        systemDesignModules {
          name
          dur
        }
      }
      deployedShowcase {
        apps {
          id
          icon
          title
          blurb
          tag
          role
          description
          meta
          ships
          isCap
        }
      }
    }
  }
`;
